import { useQuery } from "@apollo/client"
import { Info } from "@mui/icons-material"
import { Checkbox, Divider, FormControlLabel, Grid, TextField, Tooltip, Typography, useTheme, InputLabel, Select, MenuItem, ListItemText } from "@mui/material"
import { DatePicker, TimePicker } from "@mui/x-date-pickers"
import moment, { Moment } from "moment"
import { Fragment, FunctionComponent, useContext, useState, useMemo, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { AlgorithmType, UserRole } from "../../../../api/graphql/graphql-global-types"
import {
  DistrictsWithTownsResult,
  DistrictWithTowns,
  GET_DISTRICTS_WITH_TOWNS_AND_GROUPS_QUERY,
} from "../../../../api/graphql/queries/get-districts-with-towns"
import { maxTerminationTimeMinimum, TourGenerationContext } from "../../../../context/tour-generation-context"
import { UserService } from "../../../../services/user-service"
import { DEFAULT_DISTRICT_ID } from "../../../../utils/constants"
import { WeekPicker } from "../../../partials/date-picker/week-picker"
import { OutlinedSelect } from "../../../partials/layout/selection/outlined-select"
import { SelectPair } from "../../../partials/layout/selection/select-pair"
import { TourGenerationAlgorithmVersion } from "./tour-generation-alogrithm-version"
import { TourGenerationDisposalTrailerLocations } from "./tour-generation-disposal-trailer-locations"
import { TourGenerationMaterials } from "./tour-generation-materials"
import { TourGenerationTownOrGroupSelect } from "./tour-generation-town-or-group-select"
import { ContainerTypesResult, GET_CONTAINER_TYPES_QUERY } from "../../../../api/graphql/queries/get-container-types"
import { Material } from "../../../../api/graphql/queries/get-materials"

interface ITourGenerationFormProps {}

export const TourGenerationForm: FunctionComponent<ITourGenerationFormProps> = (props) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const {
    materials,
    district,
    setDistrict,
    setTowns,
    firstWeek,
    setFirstWeek,
    secondWeek,
    setSecondWeek,
    threshold,
    setThreshold,
    thresholdMin,
    setThresholdMin,
    version,
    setVersion,
    maxTerminationTime,
    setMaxTerminationTime,
    maxIterations,
    setMaxIterations,
    referenceDate,
    setReferenceDate,
    referenceDateValid,
    consideredTourDays,
    setConsideredTourDays,
    tourWithoutContainers,
    setTourWithoutContainers,
    setCollectionGroup,
    containerSizeSelect,
    setContainerSizeSelect
  } = useContext(TourGenerationContext)
  const [districtOptions, setDistrictOptions] = useState<SelectPair[]>([])

  const { data: containerTypesData } = useQuery<ContainerTypesResult>(GET_CONTAINER_TYPES_QUERY, {
    fetchPolicy: "cache-first",
  })
  const containerTypes = useMemo(() => containerTypesData?.getContainerTypes || [], [containerTypesData])
  
  useEffect(() => {
    if (!materials || materials.length === 0) {
      setContainerSizeSelect([]);
    }
  }, [materials]);
  
  // data for district select
  const { data: districtsData } = useQuery<DistrictsWithTownsResult>(GET_DISTRICTS_WITH_TOWNS_AND_GROUPS_QUERY, {
    onCompleted: (data) => {
      if (data?.getDistricts.length) {
        setDistrictOptions(data.getDistricts.map((district) => new SelectPair(district.id, district.name)))
        const defaultDistrict = data.getDistricts.find((d) => d.id === DEFAULT_DISTRICT_ID)
        const defaultDistrictData = defaultDistrict ? defaultDistrict : data.getDistricts[0]
        setDistrict(defaultDistrictData)
        defaultDistrictData.allow_tourgeneration_town_multiselect ? setTowns(defaultDistrictData.towns) : setTowns([])
      }
    },
  })

  const updateFirstWeek = (date: Moment) => {
    setFirstWeek(date)
    if (date.isSameOrAfter(secondWeek, "week")) {
      setSecondWeek(date.clone().add(1, "week"))
    }
  }

  const updateSecondWeek = (date: Moment) => {
    setSecondWeek(date)
    if (date.isSameOrBefore(firstWeek, "week")) {
      setFirstWeek(date.clone().subtract(1, "week"))
    }
  }

  const getThreshold = (newThreshold: number | null) => {
    if (!newThreshold) {
      return null
    } else if (newThreshold < 0) {
      return 0
    } else if (newThreshold > 100) {
      return 100
    }
    return newThreshold
  }

  const checkThreshold = () => {
    if (threshold === null) {
      setThreshold(0)
    }
    if (thresholdMin === null) {
      setThresholdMin(0)
    }
  }

  const checkConsiderTourDays = () => {
    if (consideredTourDays === null) {
      setConsideredTourDays(0)
    }
  }

  const getTownSelectionWidth = (district: DistrictWithTowns | null) => {
    return district && (district.allow_tourgeneration_town_multiselect || district.allow_tourgeneration_group_select)
      ? 6
      : 12
  }

  const onDistrictChanged = (district_id: string) => {
    if (!districtsData) return
    const district = districtsData.getDistricts.find((d) => d.id.toString() === district_id)
    setDistrict(district || null)
    setVersion(district?.algorithm || AlgorithmType.TO2)
    if (district?.allow_tourgeneration_town_multiselect === true) {
      setTowns(district && district.towns ? district.towns : [])
    } else {
      setTowns([])
    }

    if(district?.allow_tourgeneration_group_select === true) {
      // when district changes, reset collection group, and do not prefill unlike the towns
      setCollectionGroup(null)
    }
  }

  const onContainerSizeFilterChanged = (event:any) => {  
    const types = containerTypes.filter(t => event.target.value.includes(t.name));
    if(types){
      setContainerSizeSelect(types)
    }
  }

  return (
    <Grid container direction="column" spacing={2}>
      <Grid item xs={12}>
        <Typography sx={{ fontWeight: "bold" }}>{t("tour_generation.data.parameters")}</Typography>
      </Grid>
      {UserService.getRole() === UserRole.SUPER_ADMIN && (
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={tourWithoutContainers}
                onChange={(_, checked: boolean) => setTourWithoutContainers(checked)}
                color="primary"
                style={{
                  color: theme.palette.primary.light,
                  padding: `0 ${theme.spacing(1)}`,
                }}
              />
            }
            label={t("tour_generation.data.tour_without_containers")}
          />
        </Grid>
      )}
      <Grid item container direction="row" spacing={2} alignItems="center" sx={{ p: 2 }}>
        <Grid item xs={12} md={getTownSelectionWidth(district)}>
          <OutlinedSelect
            options={districtOptions}
            name={t("vehicle_overview.data.district")}
            onValueChange={onDistrictChanged}
            value={district ? district.id : ""}
          />
        </Grid>
        <TourGenerationTownOrGroupSelect/>
        <Grid item xs={3}>
          <TextField
            size="small"
            label={t("tour_generation.data.threshold")}
            type="number"
            inputProps={{
              min: "0",
              max: "100",
            }}
            onBlur={checkThreshold}
            fullWidth
            variant="outlined"
            value={threshold}
            onChange={(ev) => {
              setThreshold(getThreshold(ev.target.value ? parseInt(ev.target.value, 10) : null))
            }}
            disabled={tourWithoutContainers}
          />
        </Grid>
        <Grid item xs={3}>
          <TextField
            size="small"
            label={
              version === AlgorithmType.AGR
                ? t("tour_generation.data.threshold_2nd_week")
                : t("tour_generation.data.threshold_min")
            }
            type="number"
            inputProps={{
              min: "0",
              max: "100",
            }}
            onBlur={checkThreshold}
            fullWidth
            variant="outlined"
            value={thresholdMin}
            onChange={(ev) => {
              setThresholdMin(getThreshold(ev.target.value ? parseInt(ev.target.value, 10) : null))
            }}
            disabled={tourWithoutContainers}
          />
        </Grid>
      </Grid>
      <Grid item container direction="row" spacing={2} alignItems="center">
        <Grid item xs={6}>
          <WeekPicker label={t("tour_generation.data.first_week")} value={firstWeek} onWeekChanged={updateFirstWeek} />
        </Grid>
        <Grid item container xs={6} direction="row" alignItems="flex-end" spacing={1}>
          {version === AlgorithmType.AGR ? (
            <WeekPicker
              label={t("tour_generation.data.second_week")}
              value={secondWeek}
              onWeekChanged={updateSecondWeek}
              disabled={tourWithoutContainers}
            />
          ) : (
            <Fragment>
              <Grid item xs>
                <DatePicker
                  label={t("tour_generation.data.reference_date")}
                  format={t("date_format")}
                  value={referenceDate}
                  minDate={firstWeek}
                  onChange={setReferenceDate}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !referenceDateValid || referenceDate?.isBefore(firstWeek),
                      size: "small",
                    },
                  }}
                  disabled={tourWithoutContainers}
                />
              </Grid>
              <Grid item>
                <Tooltip title={t("tour_generation.reference_date_info") as string}>
                  <Info color="primary" />
                </Tooltip>
              </Grid>
            </Fragment>
          )}
        </Grid>
        {UserService.getRole() === UserRole.SUPER_ADMIN && (
          <Fragment>
            <Grid item container xs={6} direction="row" alignItems="center" spacing={1}>
              <Grid item xs>
                <TimePicker
                  ampm={false}
                  openTo="hours"
                  views={["hours", "minutes"]}
                  format="HH:mm"
                  label={t("tour_generation.data.max_termination_time")}
                  value={maxTerminationTime}
                  onChange={(date: moment.Moment | null) => setMaxTerminationTime(date as Moment)}
                  minTime={maxTerminationTimeMinimum}
                  maxTime={moment("11:59", "HH:mm")} 
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      inputProps: { readOnly: true },
                    },
                  }}
                />
              </Grid>
              <Grid item sx={{ display: "flex" }}>
                <Tooltip title={t("tour_generation.schedule_dialog.max_termination_time_info") as string}>
                  <Info color="primary" />
                </Tooltip>
              </Grid>
            </Grid>
            <Grid item xs={6}>
              <TextField
                size="small"
                label={t("tour_generation.data.max_iterations")}
                type="number"
                inputProps={{
                  min: "0",
                }}
                onBlur={checkThreshold}
                fullWidth
                variant="outlined"
                value={maxIterations || ""}
                onChange={(ev) => {
                  setMaxIterations(Number(ev.target.value) === 0 ? null : Number(ev.target.value))
                }}
              />
            </Grid>
            <Grid item container xs={6} direction="row" spacing={1} alignItems="center">
              <Grid item xs>
                <TextField
                  size="small"
                  label={t("tour_generation.data.considered_tour_days")}
                  type="number"
                  inputProps={{
                    min: "0",
                  }}
                  onBlur={checkConsiderTourDays}
                  fullWidth
                  variant="outlined"
                  value={consideredTourDays}
                  onChange={(ev) => setConsideredTourDays(Number(ev.target.value))}
                  disabled={tourWithoutContainers}
                />
              </Grid>
              <Grid item sx={{ display: "flex" }}>
                <Tooltip title={t("tour_generation.considered_tour_days_info") as string}>
                  <Info color="primary" />
                </Tooltip>
              </Grid>
            </Grid>
          </Fragment>
        )}
        <Grid item xs={6}>
          <TourGenerationDisposalTrailerLocations />
        </Grid>
      </Grid>
      <Grid item>
        <Divider />
      </Grid>
      <Grid item container direction="row" spacing={2} alignItems="center">
        <TourGenerationMaterials />
      </Grid>
      <Grid item>
        <Divider />
      </Grid>            
      {district?.container_filter_tour_generation == true && (
          <Grid item xs={6}>
             <InputLabel id="materials-label">Behältergröße (inkl. Einheit)</InputLabel>
              <Select
                labelId="contasiner-types-label"
                multiple
                value={containerSizeSelect.map(t => t.name)}
                onChange={onContainerSizeFilterChanged}
                renderValue={(selected) => {
                  const maxVisible = 2;
                  const selectedArray = selected as string[];
              
                  if (selectedArray.length <= maxVisible) {
                    return selectedArray.join(", ");
                  }
              
                  const visible = selectedArray.slice(0, maxVisible).join(", ");
                  const moreCount = selectedArray.length - maxVisible;
              
                  return `${visible}, ...(+${moreCount})`;
                }}
              >
              {materials && containerTypes
                .filter(type => 
                  type.container_type_infos?.some(info => 
                    materials.some((m:Material) => m.id.toString() === info.material_id.toString())
                  )
                )
                .map(type => (
                  <MenuItem key={type.id} value={type.name}>
                    <Checkbox checked={containerSizeSelect.map(t => t.name).indexOf(type.name) > -1} />
                    <ListItemText primary={type.name} />
                  </MenuItem>
                ))}
              </Select>
          </Grid>
        )}
    </Grid>
  )
}
